// Display-only clean-up for email bodies stored on ticket activities.
// Nothing here changes what's saved: the full original is always one
// "Read more" away. Output is plain text - email HTML is never injected
// into the page, so there's no XSS surface.

// Any real tag, doctype or comment. "<https://x>" and "<a@b.c>" don't match:
// a tag name is followed by whitespace, "/" or ">", never ":" or "@".
const HTML_HINT = /<(?:!doctype|!--|\/?[a-z][a-z0-9-]*(?:\s[^<>]*|\/)?>)/i;

// Turns an HTML email body into readable text. DOMParser builds an inert
// document (scripts never run, images never load).
export const htmlToText = (input) => {
  const source = String(input || '');
  if (!HTML_HINT.test(source) || typeof DOMParser === 'undefined') return source;
  const doc = new DOMParser().parseFromString(source, 'text/html');
  doc.querySelectorAll('script, style, head, title').forEach((el) => el.remove());
  doc.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
  doc.querySelectorAll('p, div, tr, li, h1, h2, h3, h4, h5, h6, blockquote').forEach((el) => el.append('\n'));
  doc.querySelectorAll('td, th').forEach((el) => el.append(' '));
  return doc.body.textContent || '';
};

// First line of quoted history: the earliest of the usual reply markers.
const QUOTE_MARKERS = [
  /^-{2,}\s*Original Message\s*-{2,}/im,
  /^_{5,}\s*$/m,
  /^On [^\n]{5,250}\n?[^\n]{0,120}wrote:\s*$/im,
  /^From:[^\n]+\n(?:[^\n]*\n){0,2}?(?:Sent|Date):[^\n]+/im,
  /^(?:>.*\n?){2,}/m
];

const LINK_MARKUP = /[ \t]*<(?:https?:\/\/|mailto:)[^>\s]+>/gi;

const tidy = (text) => text
  .replace(/\r\n?/g, '\n')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();


// A forwarded email starts with a "---- Forwarded message ----" divider and a
// From/Date/Subject/To header block, which would otherwise look like quoted
// history and leave nothing to show. Collapse it to "Forwarded from X" plus
// the subject, and keep the forwarded body.
const FORWARD = /^\s*-{2,}\s*Forwarded message\s*-{2,}\s*\n((?:[ \t]*(?:From|Date|Sent|Subject|To|Cc|Reply-To):[^\n]*\n)+)/i;

const unwrapForward = (text) => {
  const m = FORWARD.exec(text);
  if (!m) return text;
  const from = /From:\s*([^<\n]+)/i.exec(m[1]);
  const subject = /Subject:\s*([^\n]+)/i.exec(m[1]);
  const head = [`Forwarded${from ? ` from ${from[1].trim()}` : ''}`, subject ? subject[1].trim() : ''].filter(Boolean).join('\n');
  return `${head}\n${text.slice(m[0].length)}`;
};

// Returns { reply, full, hasQuoted }.
//  reply  - what the person actually wrote, minus the quoted thread and the
//           "<https://...>" link markup mail clients add to plain text.
//  full   - the whole original body, only whitespace-tidied.
export const cleanEmailText = (raw) => {
  const full = tidy(htmlToText(raw));
  const body = unwrapForward(full);
  let cut = body.length;
  QUOTE_MARKERS.forEach((re) => {
    const m = re.exec(body);
    if (m && m.index < cut) cut = m.index;
  });

  // Blank lines are dropped from the collapsed view: signatures are mostly
  // blank lines, and they would otherwise eat the five-line budget.
  const compact = (text) => tidy(text.replace(LINK_MARKUP, '')).replace(/\n{2,}/g, '\n');
  let reply = compact(body.slice(0, cut));
  const hasQuoted = cut < body.length;
  // A message that is nothing but quoted text (e.g. a forward) must not
  // collapse to an empty block.
  if (!reply) reply = compact(body);
  return { reply, full, hasQuoted };
};

// Rough "will this exceed five clamped lines" check; CSS does the clipping.
export const needsReadMore = ({ reply, hasQuoted }, maxLines = 5) =>
  hasQuoted || reply.split('\n').length > maxLines || reply.length > 350;

// ---- links -----------------------------------------------------------------
// Plain-text emails carry every link as a long "<https://c.gle/AAuD...>"
// string. These helpers turn them into short, clickable labels. Only http(s)
// URLs are ever linked.

const URL_RE = /<?(https?:\/\/[^\s<>]+)>?/g;

const shortLabel = (url) => {
  const bare = url.replace(/^https?:\/\//, '');
  if (bare.length <= 45) return bare.replace(/\/$/, '');
  try {
    return `${new URL(url).hostname.replace(/^www\./, '')}/…`;
  } catch {
    return `${bare.slice(0, 40)}…`;
  }
};

// Splits one line into text and link segments, keeping trailing punctuation
// ("see https://x.com.") out of the link.
const splitLine = (line) => {
  const segs = [];
  let last = 0;
  [...line.matchAll(URL_RE)].forEach((m) => {
    const url = m[1].replace(/[.,;:!?)\]]+$/, '');
    const end = m.index + m[0].length - (m[1].length - url.length);
    if (m.index > last) segs.push({ text: line.slice(last, m.index) });
    segs.push({ url, label: shortLabel(url) });
    last = end;
  });
  if (last < line.length) segs.push({ text: line.slice(last) });
  return segs;
};

// Returns [{ text } | { url, label }]. A URL alone on its own line directly
// under a short caption ("Open indexing report") becomes that caption as the
// link text, the way the email's button read.
export const linkify = (text) => {
  const lines = String(text || '').split('\n').map(splitLine);
  const merged = [];
  lines.forEach((segs) => {
    const prev = merged[merged.length - 1];
    const isLoneLink = segs.length === 1 && segs[0].url;
    const prevIsShortCaption = prev && prev.length === 1 && prev[0].text !== undefined
      && prev[0].text.trim().length >= 3 && prev[0].text.trim().length <= 40 && !/[.:]$/.test(prev[0].text.trim());
    if (isLoneLink && prevIsShortCaption) {
      merged[merged.length - 1] = [{ url: segs[0].url, label: prev[0].text.trim() }];
    } else {
      merged.push(segs);
    }
  });
  const out = [];
  merged.forEach((segs, i) => {
    if (i > 0) out.push({ text: '\n' });
    out.push(...segs);
  });
  return out;
};
